import authHeader from "../services/auth-header";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import authService from "./auth.service";

class BackgroundUploadService {
  constructor() {
    this.incident_id = null;
    this.uploads = new Map();
    this.uploadFiles = [];
    this.pendingUploads = 0;
    this.initialized = false;
    this.serviceWorkerReady = false;

    this.init();
  }

  async init() {
    console.log(`[backgroundUploadService]: Initialising service worker`);
    if (!("serviceWorker" in navigator)) {
      console.error(
        "[backgroundUploadService]: Service workers are not supported in this browser."
      );
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register(
        "/upload-worker.js"
      );
      console.log(
        "[backgroundUploadService]: Background upload service worker registered"
      );
      this.initialized = true;

      navigator.serviceWorker.addEventListener(
        "message",
        this.handleServiceWorkerMessage.bind(this)
      );

      if (registration.active) {
        this.serviceWorkerReady = true;
      } else {
        registration.addEventListener("activate", () => {
          this.serviceWorkerReady = true;
        });
      }
    } catch (error) {
      console.error(
        "[backgroundUploadService]: Failed to register service worker:",
        error
      );
      this.clearSessionStorage();
    }
  }

  handleServiceWorkerMessage(event) {
    const data = event.data;
    if (!data || !data.type) return;

    console.log("Service worker message received:", data.type, data);

    switch (data.type) {
      case "UPLOAD_PROGRESS": {
        const id = data.id || data.assetId;
        const progress = data.progress || 0;
        this.updateFileProgress(id, progress);
        break;
      }
      case "UPLOAD_COMPLETE": {
        const id = data.id || data.assetId;
        this.completeFile(id);
        break;
      }
      case "UPLOAD_ERROR": {
        const id = data.id || data.assetId;
        this.handleFileError(id, data.error);
        break;
      }
      case "ALL_UPLOADS_COMPLETE": {
        this.handleAllUploadsComplete(data.incidentId);
        break;
      }
    }
  }

  trackUpload(id, file, callbacks = {}) {
    const fileInfo = {
      id,
      name: file.name,
      progress: 0,
      status: "pending",
      callbacks,
    };

    this.uploadFiles.push(fileInfo);
    this.pendingUploads++;

    this.sendStatusUpdate();
    return id;
  }

  updateFileProgress(id, progress) {
    const file = this.uploadFiles.find((f) => f.id === id);
    if (file) {
      file.progress = progress;
      file.status = "uploading";

      if (file.callbacks && typeof file.callbacks.onProgress === "function") {
        file.callbacks.onProgress(progress);
      }

      this.sendStatusUpdate();
    }
  }

  completeFile(id) {
    const file = this.uploadFiles.find((f) => f.id === id);
    if (file) {
      file.progress = 100;
      file.status = "complete";

      if (file.callbacks && typeof file.callbacks.onComplete === "function") {
        file.callbacks.onComplete();
      }
    }

    this.pendingUploads = Math.max(0, this.pendingUploads - 1);
    this.sendStatusUpdate();

    if (this.pendingUploads === 0) {
      this.clearSessionStorage();
    }
  }

  handleFileError(id, error) {
    console.error(`Error for file ${id}:`, error);

    const file = this.uploadFiles.find((f) => f.id === id);
    if (file) {
      file.status = "error";
      file.error = error;

      if (file.callbacks && typeof file.callbacks.onError === "function") {
        file.callbacks.onError(error);
      }
    }

    this.pendingUploads = Math.max(0, this.pendingUploads - 1);
    this.sendStatusUpdate();
  }

  async handleAllUploadsComplete(incidentId) {
    if (incidentId) {
      axios
        .post(
          `https://api.varsanpr.com/customer/incident/incidents/complete`,
          { incident_id: incidentId },
          { headers: authHeader() }
        )
        .catch(async (error) => {
          if (error.response.status === 401) {
            if (
              error.config.url == "https://api.varsanpr.com/api/auth/refresh"
            ) {
              // Prevent infinite loop if refresh token request fails
              console.log("Refresh token request failed, redirecting to login");
              localStorage.removeItem("user");
              store.dispatch("auth/logout").then(() => {
                router.replace("/login");
              });
              return Promise.reject(error);
            }
            // 401, attempt to refresh token
            if (!authService.refreshPromise) {
              authService.refreshPromise = authService
                .refreshToken()
                .then(() => {
                  authService.refreshPromise = null;
                });
            }

            await authService.refreshPromise;

            return axios(error.config);
          }

          console.error("Failed to mark incident as complete:", error);
        });
    }

    this.clearSessionStorage();
    this.sendStatusUpdate(true);
  }

  sendMessageToServiceWorker(message, transferables = []) {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(message, transferables);
    } else {
      console.warn(
        "No active service worker controller found, waiting for activation..."
      );

      navigator.serviceWorker.ready
        .then((registration) => {
          if (registration.active) {
            registration.active.postMessage(message, transferables);
          } else {
            console.error("Service worker is ready but no active worker found");
          }
        })
        .catch((err) => {
          console.error("Failed to send message to service worker:", err);
        });
    }
  }

  setIncidentId(incidentId) {
    this.incident_id = incidentId;

    this.sendMessageToServiceWorker({
      type: "SET_INCIDENT_ID",
      incident_id: incidentId,
    });
  }

  sendStatusUpdate(completed = false) {
    const visibleFiles = this.uploadFiles.filter(
      (file) => file.name !== "file"
    );

    window.dispatchEvent(
      new CustomEvent("bg-upload-status", {
        detail: {
          active: this.pendingUploads > 0,
          files: this.uploadFiles,
          count: visibleFiles.length,
          incidentId: this.incident_id,
          completed: completed || this.pendingUploads === 0,
        },
      })
    );
  }

  async uploadAsset(incidentId, siteId, file, name, description) {
    if (!this.serviceWorkerReady) {
      await this.waitForServiceWorkerReady();
    }

    console.log(`[backgroundUploadService]: Uploading asset ${name}`);

    const id = `${name}-${Date.now()}`;

    sessionStorage.setItem("activeUploads", "true");
    sessionStorage.setItem("activeIncidentId", incidentId);

    try {
      const fileBuffer = await file.arrayBuffer();

      const assetData = {
        incident_id: incidentId,
        site_id: siteId,
        mime: file.type,
        description: description || "",
        name: name || file.name,
        extension: "." + file.name.split(".").pop(),
      };

      const response = await axios
        .post(
          `https://api.varsanpr.com/customer/incident/incidents/asset`,
          assetData
        )
        .catch(async (error) => {
          console.log(
            `[backgroundUploadService]: Received error when uploading asset`
          );
          console.log(error);
          if (error.response.status === 401) {
            if (
              error.config.url == "https://api.varsanpr.com/api/auth/refresh"
            ) {
              // Prevent infinite loop if refresh token request fails
              console.log("Refresh token request failed, redirecting to login");
              localStorage.removeItem("user");
              store.dispatch("auth/logout").then(() => {
                router.replace("/login");
              });
              return Promise.reject(error);
            }
            // 401, attempt to refresh token
            if (!authService.refreshPromise) {
              console.log(
                `[backgroundUploadService]: No refresh promise found, attempting token refresh`
              );
              authService.refreshPromise = authService
                .refreshToken()
                .then(() => {
                  authService.refreshPromise = null;
                });
            }

            await authService.refreshPromise;

            console.log(
              `[backgroundUploadService]: Refresh complete, retrying failed upload request`
            );

            return axios(error.config);
          }
        });

      this.sendMessageToServiceWorker(
        {
          type: "UPLOAD_FILE",
          id,
          assetId: response.data.asset_id,
          fileBuffer,
          fileName: file.name,
          fileType: file.type,
          sas: response.data.sas,
          incidentId,
        },
        [fileBuffer]
      );

      return id;
    } catch (error) {
      console.error("Failed to start upload:", error);
      throw error;
    }
  }

  async reuploadAsset(incidentId, siteId, file, assetId, sasUrl) {
    if (!this.serviceWorkerReady) {
      await this.waitForServiceWorkerReady();
    }

    const id = `reupload-${assetId}-${Date.now()}`;

    sessionStorage.setItem("activeUploads", "true");
    sessionStorage.setItem("activeIncidentId", incidentId);

    try {
      const fileBuffer = await file.arrayBuffer();

      this.sendMessageToServiceWorker(
        {
          type: "REUPLOAD_FILE",
          id,
          assetId,
          fileBuffer,
          fileName: file.name,
          fileType: file.type,
          sasUrl,
          incidentId,
          siteId,
        },
        [fileBuffer]
      );

      return id;
    } catch (error) {
      console.error("Failed to start reupload:", error);
      throw error;
    }
  }

  async waitForServiceWorkerReady() {
    if (this.serviceWorkerReady) return true;

    return new Promise((resolve) => {
      const start = Date.now();

      const check = () => {
        if (this.serviceWorkerReady) {
          resolve(true);
          return;
        }

        if (Date.now() - start > timeout) {
          console.warn("Service worker not ready after timeout");
          resolve(false);
          return;
        }

        setTimeout(check, 100);
      };

      check();
    });
  }

  getActiveUploads() {
    return {
      count: this.pendingUploads,
      incidentId: this.incident_id,
      files: this.uploadFiles,
    };
  }

  calculateProgress() {
    if (this.uploadFiles.length === 0) return 0;

    let totalProgress = 0;
    this.uploadFiles.forEach((file) => {
      totalProgress += file.status === "complete" ? 100 : file.progress || 0;
    });

    return Math.round(totalProgress / this.uploadFiles.length);
  }

  clearSessionStorage() {
    sessionStorage.removeItem("activeUploads");
    sessionStorage.removeItem("activeIncidentId");
  }

  getStatus() {
    const active = this.pendingUploads > 0;

    return {
      active,
      uploads: this.uploadFiles,
      incidentId: this.incident_id,
      progress: this.calculateProgress(),
    };
  }
}

export default new BackgroundUploadService();
